import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import UserAvatar from '@/components/feature/UserAvatar';
import { getAllUsers } from '@/lib/community';
import { usePageMeta } from '@/hooks/usePageMeta';
import { SITE_NAME } from '@/lib/constants';
import { cn } from '@/lib/format';

const teamRoles = ['社区主理人', '内容运营', '社区运营', '技术负责人'];

const values = [
  { icon: 'ri-heart-3-line', title: '真诚分享', desc: '鼓励真实经验与观点，让每条内容都有温度，而不是空洞的流量。' },
  { icon: 'ri-shield-star-line', title: '包容多元', desc: '无论你是生活达人还是冷门爱好者，都能在这里找到同好。' },
  { icon: 'ri-seedling-line', title: '共同成长', desc: '我们相信持续的交流会让每个人变得更好，也让社区更温暖。' },
];

const faqs = [
  { q: '蜜糖社区是什么？', a: '蜜糖社区是一个聚焦生活、娱乐、文化、技术等多元兴趣的交流平台，用户可以浏览、发布话题并参与讨论，找到兴趣相投的朋友。' },
  { q: '我需要注册才能使用吗？', a: '当前版本为前端功能演示，浏览与互动均无需注册。正式上线后将支持账号体系，方便你管理自己的内容。' },
  { q: '发布的内容会一直保存吗？', a: '在演示版本中，发布话题与回复仅作交互展示，不会保存到服务器。刷新页面后内容会恢复为示例数据。' },
  { q: '如何联系运营团队？', a: '你可以通过页面下方的邮箱或社交媒体渠道联系我们，我们通常会在 1-2 个工作日内回复。' },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const team = getAllUsers().slice(0, 4);

  usePageMeta({
    title: `关于我们 - ${SITE_NAME}`,
    description: '了解蜜糖社区的定位、服务宗旨、运营团队与联系方式。我们致力于打造一个活跃、包容、多元的兴趣交流社区。',
    keywords: '蜜糖社区,关于我们,社区介绍,运营团队,联系方式,兴趣交流',
  });

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <section className="mt-5 rounded-xl border border-background-200 bg-primary-100 p-7 md:p-12 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 text-primary-700 px-3 py-1 text-xs font-label font-bold mb-4">
            <i className="ri-drop-fill" />
            关于蜜糖社区
          </span>
          <h1 className="text-2xl md:text-4xl font-extrabold text-foreground-900 leading-tight">
            热爱生活的人，都在这里相遇
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-sm md:text-base text-foreground-700 leading-relaxed">
            我们相信，每一个看似普通的兴趣，都值得被认真对待。蜜糖社区希望成为这样一个地方：你可以放心分享生活里的大小事，也能在别人的故事里，找到继续热爱生活的理由。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-5 py-2.5 whitespace-nowrap transition-colors cursor-pointer">
              <i className="ri-compass-3-line" />
              去逛逛话题
            </Link>
            <Link to="/post" className="inline-flex items-center gap-1.5 rounded-md bg-background-50 border border-primary-200 text-primary-700 text-sm font-label font-bold px-5 py-2.5 whitespace-nowrap transition-colors cursor-pointer hover:bg-background-100">
              <i className="ri-add-line" />
              发布话题
            </Link>
          </div>
        </section>

        {/* Mission + values */}
        <section className="mt-12">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-extrabold text-foreground-900">我们的定位与宗旨</h2>
            <p className="mt-2 text-sm text-foreground-500">现代门户 + 轻社交，让信息密度与浏览体验并存。</p>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {values.map((v) => (
              <div key={v.title} className="bg-background-50 border border-background-200 rounded-lg p-6">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-xl`} />
                </span>
                <h3 className="text-base font-extrabold text-foreground-900">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team */}
        <section className="mt-12">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-extrabold text-foreground-900">运营团队</h2>
            <p className="mt-2 text-sm text-foreground-500">一群爱聊天、爱分享的普通人。</p>
          </div>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {team.map((member, idx) => (
              <div key={member.id} className="bg-background-50 border border-background-200 rounded-lg p-5 text-center">
                <UserAvatar src={member.avatar} alt={member.name} size="lg" className="mx-auto" />
                <h3 className="mt-3 text-sm font-extrabold text-foreground-900">{member.name}</h3>
                <p className="mt-0.5 text-xs text-primary-700 font-label font-bold">{teamRoles[idx]}</p>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">{member.bio}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mt-12">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-extrabold text-foreground-900">联系我们</h2>
            <p className="mt-2 text-sm text-foreground-500">合作、建议或反馈，欢迎随时找我们聊聊（以下信息均为示意）。</p>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                <i className="ri-mail-line text-xl" />
              </span>
              <h3 className="text-base font-extrabold text-foreground-900">邮箱</h3>
              <p className="mt-2 text-sm text-foreground-600">hello@honeycommunity.cn</p>
              <p className="mt-1 text-xs text-foreground-400">合作与商务请附上简要说明</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-xl" />
              </span>
              <h3 className="text-base font-extrabold text-foreground-900">用户反馈</h3>
              <p className="mt-2 text-sm text-foreground-600">feedback@honeycommunity.cn</p>
              <p className="mt-1 text-xs text-foreground-400">产品建议、举报与申诉</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-4">
                <i className="ri-share-line text-xl" />
              </span>
              <h3 className="text-base font-extrabold text-foreground-900">社交媒体</h3>
              <div className="mt-2 flex items-center gap-2">
                {['ri-wechat-2-line', 'ri-weibo-line', 'ri-message-3-line'].map((icon) => (
                  <span key={icon} className="w-9 h-9 rounded-md bg-background-100 text-foreground-600 flex items-center justify-center">
                    <i className={`${icon} text-lg`} />
                  </span>
                ))}
              </div>
              <p className="mt-2 text-xs text-foreground-400">搜索「蜜糖社区」关注我们</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12 max-w-3xl mx-auto">
          <div className="text-center">
            <h2 className="text-xl md:text-2xl font-extrabold text-foreground-900">常见问题</h2>
            <p className="mt-2 text-sm text-foreground-500">你可能想了解的那些事。</p>
          </div>
          <div className="mt-6 flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <div key={faq.q} className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-bold text-foreground-900">{faq.q}</span>
                  <i className={cn('text-lg text-primary-600 transition-transform', openFaq === idx ? 'ri-subtract-line' : 'ri-add-line')} />
                </button>
                {openFaq === idx && (
                  <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed animate-fade-up">{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}